import { useEffect, useState } from "react";

export interface CountdownProps {
  targetDate: Date;
  onComplete?: () => void;
}

export interface TimeConfig {
  minutes?: number;
  seconds?: number;
  totalInMilliSeconds: number;
}

export function useCountdown({ targetDate, onComplete }: CountdownProps) {
  // console.log("targetDate in hook", targetDate);
  const [timeLeft, setTimeLeft] = useState<{
    minutes: number;
    seconds: number;
    totalInMilliSeconds: number;
  }>({ minutes: 0, seconds: 0, totalInMilliSeconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = targetDate.getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeLeft({ minutes: 0, seconds: 0, totalInMilliSeconds: 0 });
        if (onComplete) onComplete();
        return null;
      }

      return {
        minutes: Math.floor(difference / 1000 / 60),
        seconds: Math.floor((difference / 1000) % 60),
        totalInMilliSeconds: difference,
      };
    };

    // Initial calculation
    const timeLeft = calculateTimeLeft();
    if (timeLeft) setTimeLeft(timeLeft);

    // Update every second
    const timer = setInterval(() => {
      const timeLeft = calculateTimeLeft();
      if (timeLeft) {
        setTimeLeft(timeLeft);
      } else {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, onComplete]);

  const formattedTime = `${timeLeft.minutes.toString().padStart(2, "0")}:${timeLeft.seconds
    .toString()
    .padStart(2, "0")}`;

  return { timeLeft, formattedTime };
}

// Helper to create a target date from a time config
export function createTargetFromTime(time: Partial<TimeConfig>, initialTarget?: Date): Date {
  const target = initialTarget ?? new Date();
  const totalSeconds = (time.minutes || 0) * 60 + (time.seconds || 0);
  target.setSeconds(target.getSeconds() + totalSeconds);
  return target;
}

// Convenience function for minutes only
export function createTargetDate(minutes: number): Date {
  return createTargetFromTime({ minutes });
}

// Convenience function for seconds only
export function createTargetDateFromSeconds(seconds: number): Date {
  return createTargetFromTime({ seconds });
}
